import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useLanguagePairContext } from 'src/context/languagePair';
import { useExternalHref } from 'src/hooks/useExternalHref';
import { useHref } from 'src/hooks/useHref';
import { useTargetMessages } from 'src/hooks/useTargetMessages';
import { getTargetMessages } from 'src/utils/getTargetMessages';
import { ROUTES } from 'src/constants/routes.const';
import { Breadcrumbs, BreadcrumbsProps } from '../Breadcrumbs';
import * as courseLocalesWithMessages from './course-messages';
import * as learnLocalesWithMessages from './learn-messages';

export type LanguagePairBreadcrumbsProps = Partial<BreadcrumbsProps>;

export const LanguagePairBreadcrumbs = ({
  breadcrumbs = [],
  ...rest
}: LanguagePairBreadcrumbsProps) => {
  const intl = useIntl();
  const { targetLanguageLocale } = useLanguagePairContext();
  const targetLanguageBaseLocale = targetLanguageLocale.split('-')[0];
  const courseMessages = useTargetMessages(courseLocalesWithMessages);
  const learnMessages = getTargetMessages(learnLocalesWithMessages, targetLanguageBaseLocale);

  const courseHref = useHref({ pathname: ROUTES.course });
  const learnHref = useExternalHref({
    linkType: 'memrise-learn-language',
  });

  const breadcrumbsForRendering = useMemo(() => {
    return [
      {
        name: intl.formatMessage(learnMessages.learnPageBreadcrumb),
        url: learnHref,
      },
      {
        name: intl.formatMessage(courseMessages.coursePageBreadcrumb),
        url: courseHref,
      },
      ...breadcrumbs,
    ].filter(Boolean) as BreadcrumbsProps['breadcrumbs'];
  }, [
    breadcrumbs,
    courseHref,
    intl,
    learnHref,
    courseMessages.coursePageBreadcrumb,
    learnMessages,
  ]);

  return <Breadcrumbs breadcrumbs={breadcrumbsForRendering} {...rest} />;
};
